import {
  Fragment,
  lazy,
  ReactElement,
  Suspense,
  useEffect,
  useState,
} from 'react';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import { setLanguageCode, setUserInfo } from 'reducers/pageAction';
import { updateAuthoringCustomerLogo } from 'api/patch';
import { getUserAvatar } from 'api';
import i18next, { languages } from 'utils/i18n';
import { renderSnackbar } from 'utils';
import { deferImport } from 'utils/helper';
import UserInterface from 'interface/user';
import { ApiResponse } from 'interface/common/ApiResponseInterface';
import { availableLanguages } from '../../../constants';
import { AppDispatch } from 'const';
/* Components */
import {
  Avatar,
  ClassNameMap,
  Divider,
  FormControl,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { CSSProperties } from '@mui/styles';
import {
  AccountCircle,
  ExitToAppRounded,
  Language,
  Password,
  PersonOutlineOutlined,
  Settings,
} from '@mui/icons-material';
import UploadImage from 'components/upload-image';
import LoadingModal from 'components/commons/loading-modal';

/* Lazy load component */
const ImpersonateUserPopup = lazy((): Promise<any> => {
  return deferImport(import('components/popups/impersonate-popup'));
});
/* End */

interface ActionListItem {
  id: string;
  label: string | ReactElement;
  icon: ReactElement;
  hide?: boolean;
  divider?: boolean;
  containerStyle?: CSSProperties;
  onClick?: () => void;
}

interface PopoverItemProps {
  onClose: () => void;
}

const PopoverItem = ({ onClose }: PopoverItemProps) => {
  const { t } = useTranslation([
    'sds_authoring',
    'header',
    'supplier_manufacturer_listing',
  ]);
  const history = useHistory();
  const classes: ClassNameMap = useStyles();
  const dispatch: AppDispatch = useDispatch();
  const userInfo: UserInterface = useSelector(
    (state: any) => state.pageReducer.userInfo
  );
  const languageCode: string = useSelector(
    (state: any) => state.pageReducer.languageCode
  );
  const [logo, setLogo] = useState<Blob | null>(null);
  const [logoSrc, setLogoSrc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [openImpersonatePopup, setOpenImpersonatePopup] =
    useState<boolean>(false);
  const impersonateToken = localStorage.getItem('impersonate_backup_token');

  const handleLogout = (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    dispatch(setUserInfo(null));
    history.push(`/login/`);
  };

  const handleLanguageChange = (value: string): void => {
    dispatch(setLanguageCode(value));
    let setLang = languages.includes(value) ? value : 'en';
    localStorage.setItem('languageCode', setLang);
    if (i18next.language !== setLang) i18next.changeLanguage(setLang);
  };

  const changeLogoInSDS = (logo: Blob | null): void => {
    if (!userInfo?.customer) return;
    const updateData = new FormData();
    if (logo) updateData.append('authoring_logo', logo);
    updateAuthoringCustomerLogo(updateData).then(
      (response: ApiResponse<any>) => {
        if (response.status == 200) {
          renderSnackbar([t('header:upload_logo_success')]);
          setLogo(logo);
        }
      }
    );
  };

  useEffect(() => {
    userInfo?.customer?.authoring_logo &&
      setLogoSrc(getUserAvatar(userInfo.customer.authoring_logo));
  }, [userInfo]);

  useEffect(() => {
    if (!logo) return;
    setLogoSrc(URL.createObjectURL(logo));
  }, [logo]);

  const actionList: Array<ActionListItem> = [
    {
      id: 'profile',
      label: userInfo && `${userInfo.first_name} ${userInfo.last_name}`,
      icon:
        userInfo && userInfo.avatar ? (
          <ListItemAvatar>
            <Avatar alt="avatar" src={getUserAvatar(userInfo.avatar)} />
          </ListItemAvatar>
        ) : (
          <ListItemIcon>
            <AccountCircle sx={{ width: '40px', height: '40px' }} />
          </ListItemIcon>
        ),
    },
    {
      id: 'set-password',
      label: t('account:set_password'),
      hide: !userInfo?.need_to_set_password,
      icon: (
        <ListItemIcon className={classes.popoverIcon}>
          <Password />
        </ListItemIcon>
      ),
      containerStyle: {
        animation: 'flash 3s',
        '@keyframes flash': {
          '0%': { backgroundColor: 'transparent' },
          '50%': { backgroundColor: '#FFBF00' },
          '100%': { backgroundColor: 'transparent' },
        },
        '&:hover': {
          animation: 'none',
          backgroundColor: 'rgba(255, 191, 0, 0.2)',
        },
      },
      onClick: () => {
        history.push('/account/security');
        if (userInfo?.id)
          localStorage.setItem(`is_open_profile_${userInfo.id}`, 'true');
      },
    },
    {
      id: 'organization',
      label: userInfo.customer.name,
      divider: true,
      icon: (
        <ListItemAvatar>
          <UploadImage
            onUpload={(logo_file: Blob | null) => changeLogoInSDS(logo_file)}
            size={40}
            imageUrl={logoSrc}
            component={AccountCircle}
            tooltip={t('header:click_to_upload_logo')}
          />
        </ListItemAvatar>
      ),
    },
    {
      id: 'language',
      divider: true,
      label: (
        <FormControl variant="standard" sx={{ width: '100%' }}>
          <Select
            id="language"
            labelId="language-input-label"
            value={languageCode}
            onChange={(event: SelectChangeEvent<string>) =>
              handleLanguageChange(event.target.value)
            }
            MenuProps={{ classes: { paper: classes.optionInput } }}
          >
            {availableLanguages.map((el, i) => {
              return (
                <MenuItem key={i} value={el.value}>
                  {t(`languages:${el.value}`)}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      ),
      icon: (
        <ListItemIcon className={classes.popoverIcon}>
          <Language width={30} />
        </ListItemIcon>
      ),
    },
    {
      id: 'supplier-manufacturer',
      label: t('supplier_manufacturer_listing:title'),
      divider: true,
      icon: (
        <ListItemIcon className={classes.popoverIcon}>
          <Settings />
        </ListItemIcon>
      ),
      onClick: () => {
        onClose();
        history.push(`/supplier-manufacturer`);
      },
    },
    {
      id: 'impersonate',
      label: t('header:impersonate'),
      hide: !userInfo?.allow_impersonate || !!impersonateToken,
      icon: (
        <ListItemIcon className={classes.popoverIcon}>
          <PersonOutlineOutlined width={30} />
        </ListItemIcon>
      ),
      onClick: () => {
        onClose();
        setOpenImpersonatePopup(true);
      },
    },
    {
      id: 'cancel-impersonate',
      label: t('header:cancel_impersonate'),
      hide: !impersonateToken,
      icon: (
        <ListItemIcon className={classes.popoverIcon}>
          <PersonOutlineOutlined width={30} />
        </ListItemIcon>
      ),
      onClick: () => {
        setLoading(true);
        localStorage.removeItem('access_token');
        if (!!impersonateToken) {
          localStorage.setItem('refresh_token', impersonateToken);
          localStorage.removeItem('impersonate_backup_token');
        } else {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('impersonate_backup_token');
        }
        window.location.reload();
        onClose();
      },
    },
    {
      id: 'logout',
      label: userInfo ? (
        <NavLink
          to={''}
          className={classes.menuLink}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleLogout();
          }}
        >
          {t('header:logout')}
        </NavLink>
      ) : (
        <NavLink
          to={''}
          className={classes.menuLink}
          onClick={e => history.push(`/login/`)}
        >
          {t('header:login')}
        </NavLink>
      ),
      icon: (
        <ListItemIcon className={classes.popoverIcon}>
          <ExitToAppRounded />
        </ListItemIcon>
      ),
    },
  ];

  if (!userInfo) return null;

  return (
    <>
      <div className={classes.popover}>
        <List>
          {actionList.map(item =>
            item.hide ? null : (
              <Fragment key={`header-item-${item.id}`}>
                {item.divider && <Divider />}
                <ListItem disablePadding onClick={item.onClick}>
                  <ListItemButton sx={item.containerStyle}>
                    {item.icon}
                    <ListItemText>{item.label}</ListItemText>
                  </ListItemButton>
                </ListItem>
              </Fragment>
            )
          )}
        </List>
      </div>

      <Suspense fallback={<LoadingModal />}>
        {openImpersonatePopup && (
          <ImpersonateUserPopup
            onClose={() => setOpenImpersonatePopup(false)}
          />
        )}

        {loading && <LoadingModal />}
      </Suspense>
    </>
  );
};

export default PopoverItem;
